import React, { useEffect, useMemo, useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '../../services/api';
import {
  TrialTrackerEvent,
  TrialTrackerTrial,
  TrialTrackerUpdate,
  DESIGNATION_META,
  statusColorClasses,
  registryForNctId,
  nctRegistryUrl,
  updateTypeMeta,
  sponsorsMatch,
  formatScrapeDate,
} from '../../utils/trialTrackerTimeline';

interface Props {
  nctId: string;
  trial?: TrialTrackerTrial;
  events: TrialTrackerEvent[];
  allTrials: TrialTrackerTrial[];
  onClose: () => void;
  onSelectSimilar: (nctId: string) => void;
  onFilterBySponsor?: (sponsor: string) => void;
}

const UPDATES_PREVIEW_LIMIT = 6;

const TrackerDrawer: React.FC<Props> = ({
  nctId,
  trial,
  events,
  allTrials,
  onClose,
  onSelectSimilar,
  onFilterBySponsor,
}) => {
  const [updates, setUpdates] = useState<TrialTrackerUpdate[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  useEffect(() => {
    setShowAllUpdates(false);
  }, [nctId]);

  useEffect(() => {
    // Keep drawer header visible when switching trials.
    const panel = document.getElementById('tracker-drawer-scroll');
    panel?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [nctId]);

  useEffect(() => {
    let cancelled = false;
    setUpdatesLoading(true);
    apiService.fetchTrialTrackerTrialUpdates(nctId)
      .then((res) => {
        if (!cancelled && res?.success) setUpdates(res.updates || []);
      })
      .catch(() => {
        if (!cancelled) setUpdates([]);
      })
      .finally(() => {
        if (!cancelled) setUpdatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [nctId]);

  const history = useMemo(() => {
    if (updates.length > 0) {
      return [...updates].sort((a, b) => (b.detected_at || '').localeCompare(a.detected_at || ''));
    }
    return events
      .filter((e) => e.nct_id === nctId && e.event_type !== 'CURRENT' && e.source !== 'snapshot')
      .sort((a, b) => (b.detected_at || '').localeCompare(a.detected_at || ''))
      .map((e): TrialTrackerUpdate => ({
        kind: 'status_event',
        id: e.id,
        event_type: e.event_type,
        headline: e.headline,
        from_value: e.from_value,
        to_value: e.to_value,
        detected_at: e.detected_at,
        source: e.source,
      }));
  }, [updates, events, nctId]);

  const ev = events.find((e) => e.nct_id === nctId);
  const phase = trial?.phase || ev?.phase || null;
  const enrollment = trial?.enrollment != null ? String(trial.enrollment) : (ev?.enrollment != null ? String(ev.enrollment) : null);
  const statusValue = trial?.status || ev?.status || null;
  const indication = trial?.indication || ev?.indication || ev?.indication_name || null;
  const studyStart = trial?.study_start_date || ev?.study_start_date || null;
  const primaryCompletion = trial?.primary_completion_date || ev?.primary_completion_date || null;
  const studyCompletion = trial?.study_completion_date || ev?.study_completion_date || null;
  const title = trial?.trial_name || trial?.full_title || ev?.trial_name || ev?.full_title || nctId;
  const assetName = trial?.asset_name || ev?.asset_name || null;
  const latest = trial || (history[0] as unknown as TrialTrackerTrial | undefined);
  const status = statusColorClasses(statusValue || latest?.status);
  const registry = registryForNctId(nctId);
  const registryUrl = nctRegistryUrl(nctId);
  const sponsor = trial?.sponsor || ev?.sponsor || null;

  const updateSummary = useMemo(() => {
    const statusCount = history.filter((h) => h.kind !== 'readout').length;
    const readoutCount = history.filter((h) => h.kind === 'readout').length;
    const parts: string[] = [];
    if (statusCount) parts.push(`${statusCount} registry change${statusCount === 1 ? '' : 's'}`);
    if (readoutCount) parts.push(`${readoutCount} readout${readoutCount === 1 ? '' : 's'}`);
    return parts.join(' · ') || 'No updates yet';
  }, [history]);

  const visibleHistory = showAllUpdates ? history : history.slice(0, UPDATES_PREVIEW_LIMIT);

  const similar = useMemo(() => {
    const key = (indication || '').toLowerCase().trim();
    if (!key) return [];
    return allTrials
      .filter((t) => t.nct_id !== nctId && (t.indication || '').toLowerCase().trim() === key)
      .slice(0, 4);
  }, [allTrials, indication, nctId]);

  const sponsorTrials = useMemo(() => {
    if (!sponsor) return [];
    return allTrials
      .filter((t) => t.nct_id !== nctId && sponsorsMatch(t.sponsor, sponsor))
      .slice(0, 6);
  }, [allTrials, sponsor, nctId]);

  const designations = trial?.designations || events.find((e) => e.nct_id === nctId)?.designations || [];

  return (
    <div className="w-80 shrink-0 min-h-0 self-stretch flex flex-col border-l border-tracker-line bg-tracker-sidebar overflow-hidden">
      <div id="tracker-drawer-scroll" className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="flex items-start justify-between mb-2.5">
          <span className="text-[10.5px] font-bold tracking-wide text-tracker-muted">TRACKED TRIAL</span>
          <button onClick={onClose} className="text-tracker-muted hover:text-tracker-ink"><X size={15} /></button>
        </div>
        <div className="font-sourceserif text-xl font-bold leading-tight">{title}</div>
        <div className="text-xs text-tracker-muted font-plex mt-0.5 flex items-center gap-1 flex-wrap">
          {registryUrl ? (
            <a
              href={registryUrl}
              target="_blank"
              rel="noreferrer"
              className="text-tracker-accent hover:text-tracker-accentDark hover:underline"
              title="Open on ClinicalTrials.gov"
            >
              {nctId}
            </a>
          ) : (
            <span>{nctId}</span>
          )}
          {sponsor ? (
            <>
              <span>·</span>
              <button
                type="button"
                onClick={() => onFilterBySponsor?.(sponsor)}
                className="text-sky-700 hover:text-sky-900 hover:underline font-sans font-semibold text-left"
                title="Show all trials from this company"
              >
                {sponsor}
              </button>
            </>
          ) : (
            <span>· —</span>
          )}
        </div>
        {assetName && (
          <div className="text-[11px] font-semibold text-tracker-accent mt-1">{assetName}</div>
        )}

        {designations.length > 0 && (
          <div className="flex flex-wrap gap-1.5 my-3">
            {designations.map((d) => (
              <span key={d.code} className="text-[10.5px] font-semibold px-2.5 py-1 rounded-full bg-tracker-designationBg text-tracker-designationFg border border-tracker-designationBorder">
                ⚡ {DESIGNATION_META[d.code]?.short || d.code}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-1.5 text-xs p-3 bg-white border border-tracker-line rounded-lg mt-3">
          <Row label="Registry" value={registry.label} />
          <Row label="Phase" value={phase || '—'} />
          <Row label="Enrollment" value={enrollment || '—'} />
          <Row label="Status" value={<span className={`px-1.5 py-0.5 rounded ${status.bg} ${status.fg} font-semibold`}>{statusValue || '—'}</span>} />
          <Row label="Indication" value={indication || '—'} />
        </div>

        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mt-4 mb-2">KEY DATES</div>
        <div className="flex flex-col gap-1.5 text-xs p-3 bg-white border border-tracker-line rounded-lg font-plex">
          <Row label="Study start" value={studyStart?.slice(0, 10) || '—'} />
          <Row label="Primary completion" value={primaryCompletion?.slice(0, 10) || '—'} />
          <Row label="Study completion" value={studyCompletion?.slice(0, 10) || '—'} />
        </div>

        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mt-4 mb-1 flex items-center justify-between gap-2">
          <span>
            UPDATES
            <span className="font-normal normal-case tracking-normal text-tracker-line2 ml-1">CT.gov + readouts</span>
          </span>
        </div>
        {!updatesLoading && history.length > 0 && (
          <p className="text-[10.5px] text-tracker-muted mb-2">{updateSummary}</p>
        )}
        <div className="relative pl-6 mt-2">
          <div className="absolute left-[7px] top-1 bottom-1 w-px bg-tracker-line" />
          {updatesLoading && <div className="text-[11px] text-tracker-muted py-2">Loading updates…</div>}
          {!updatesLoading && history.length === 0 && (
            <div className="text-[11px] text-tracker-muted py-2">No recorded events yet.</div>
          )}
          {visibleHistory.map((e) => {
            const meta = updateTypeMeta(e.event_type, e.kind);
            return (
              <div key={`${e.kind}-${e.id}`} className="relative pb-4">
                <span className={`absolute -left-[22px] top-0.5 w-3 h-3 rounded-full border-2 border-white ${meta.bg}`} style={{ boxShadow: '0 0 0 1px currentColor' }} />
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <span className={`text-[9.5px] font-bold ${meta.fg}`}>{meta.label}</span>
                  {e.kind === 'readout' && (
                    <span className="text-[9px] font-semibold text-orange-600/80 uppercase">readout</span>
                  )}
                  {e.detected_at && (
                    <span className="text-[9.5px] text-tracker-muted font-plex ml-auto" title="Registry posted date">
                      {formatScrapeDate(e.detected_at)}
                    </span>
                  )}
                </div>
                <div className="text-xs font-semibold text-tracker-ink leading-snug">{e.headline}</div>
                {e.from_value && e.to_value && (
                  <div className="text-[11px] mt-0.5 text-tracker-muted">
                    <span className="line-through decoration-tracker-line2">{e.from_value}</span>
                    {' → '}
                    <span className={`font-semibold ${meta.fg}`}>{e.to_value}</span>
                  </div>
                )}
                {!e.from_value && e.to_value && (
                  <div className={`text-[11px] mt-0.5 font-semibold ${meta.fg}`}>{e.to_value}</div>
                )}
                {e.kind === 'readout' && (e.timing_start || e.timing_end) && (
                  <div className="text-[10.5px] mt-0.5 text-tracker-muted font-plex">
                    {e.timing_start?.slice(0, 10) || '—'}
                    {e.timing_end ? ` → ${e.timing_end.slice(0, 10)}` : ''}
                  </div>
                )}
                {e.source_url && (
                  <a
                    href={e.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-tracker-accent hover:underline mt-0.5 inline-block"
                  >
                    {e.source_name || 'Source'}
                  </a>
                )}
              </div>
            );
          })}
          {!updatesLoading && history.length > UPDATES_PREVIEW_LIMIT && (
            <button
              type="button"
              onClick={() => setShowAllUpdates((v) => !v)}
              className="text-[11px] font-semibold text-tracker-accent hover:text-tracker-accentDark flex items-center gap-1 mb-2"
            >
              {showAllUpdates ? (
                <>Show fewer <ChevronUp size={12} /></>
              ) : (
                <>Show all {history.length} updates <ChevronDown size={12} /></>
              )}
            </button>
          )}
        </div>

        {sponsorTrials.length > 0 && (
          <>
            <div className="flex items-center justify-between gap-2 mt-3 mb-2 pt-3 border-t border-tracker-line">
              <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted">
                MORE FROM {sponsor.length > 28 ? `${sponsor.slice(0, 28)}…` : sponsor.toUpperCase()}
              </div>
              <button
                type="button"
                onClick={() => onFilterBySponsor?.(sponsor)}
                className="text-[10px] font-semibold text-sky-700 hover:underline shrink-0"
              >
                View all
              </button>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              {sponsorTrials.map((s) => (
                <div key={s.nct_id} className="flex items-center gap-2.5 px-2.5 py-2 bg-white border border-tracker-line rounded-lg">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold truncate">{s.trial_name || s.asset_name}</div>
                    <div className="text-[10.5px] text-tracker-muted font-plex">{s.nct_id} · {s.phase}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onSelectSimilar(s.nct_id)}
                    className="flex-none text-[11px] font-semibold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {similar.length > 0 && (
          <>
            <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mt-3 mb-2 pt-3 border-t border-tracker-line">
              SIMILAR TRIALS — SAME INDICATION
            </div>
            <div className="flex flex-col gap-2 mt-2">
              {similar.map((s) => (
                <div key={s.nct_id} className="flex items-center gap-2.5 px-2.5 py-2 bg-white border border-tracker-line rounded-lg">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold truncate">{s.trial_name || s.asset_name}</div>
                    <div className="text-[10.5px] text-tracker-muted">{s.phase}</div>
                  </div>
                  <button
                    onClick={() => onSelectSimilar(s.nct_id)}
                    className="flex-none text-[11px] font-semibold px-2.5 py-1 rounded-full bg-tracker-statusBg text-tracker-accent border border-tracker-cardBorderUnread"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-tracker-muted">{label}</span>
    <span className="font-semibold text-right">{value}</span>
  </div>
);

export default TrackerDrawer;
